import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import LinkIcon from '@atlaskit/icon/core/link';
import { Box } from '@atlaskit/primitives/compiled';
import Tooltip from '@atlaskit/tooltip';
import { usePlatformInstrumentation } from '@atlassian/platform-analytics';

import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { useAudioShareLinkCopy } from '../../hooks/useAudioShareLinkCopy';

const styles = cssMap({
	cardWrapper: {
		height: '100%',
	},
});

const messages = defineMessages({
	tooltip: {
		id: 'audio.audio-player.audio-share-link-button.link',
		defaultMessage: 'Link to audio',
		description: 'Tooltip for the audio share link button',
	},
	label: {
		id: 'audio.audio-player.audio-share-link-button.label',
		defaultMessage: 'Copy link to audio',
		description: 'Label for the audio share link button',
	},
	copied: {
		id: 'audio.audio-player.audio-share-link-button.copied',
		defaultMessage: 'Copied!',
		description: 'Tooltip text shown after the link has been copied to clipboard',
	},
});

export const AudioShareLinkButton = () => {
	const { formatMessage } = useIntl();
	const { activeContent, activeFeature } = useAudioPlayerStore();
	const { copyAudioShareLinkToClipboard } = useAudioShareLinkCopy();
	const { sendUIEvent } = usePlatformInstrumentation();

	const defaultTooltip = formatMessage(messages.tooltip);
	const [tooltipContent, setTooltipContent] = useState(defaultTooltip);
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const updateTooltip = useRef<() => void>();

	useLayoutEffect(() => {
		updateTooltip.current?.();
	}, [tooltipContent]);

	const handleCopyLink = useCallback(() => {
		copyAudioShareLinkToClipboard(
			activeContent.mainContentLink,
			activeFeature,
			activeContent.supportingContent,
		);
		sendUIEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'aiAudioCopyLink',
			attributes: {
				contentType: activeContent.contentType,
				trackType: activeFeature,
			},
		});

		setTooltipContent(formatMessage(messages.copied));

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}
		timeoutRef.current = setTimeout(() => {
			setTooltipContent(formatMessage(messages.tooltip));
		}, 2000);
	}, [activeContent, activeFeature, copyAudioShareLinkToClipboard, sendUIEvent, formatMessage]);

	return (
		<Box id="audio-player-changeboarding-step-3" xcss={styles.cardWrapper}>
			<Tooltip
				content={({ update }) => {
					updateTooltip.current = update;
					return tooltipContent;
				}}
				position="bottom"
			>
				<IconButton
					icon={LinkIcon}
					label={formatMessage(messages.label)}
					onClick={handleCopyLink}
					isDisabled={activeContent.mainContentLink === ''}
					shape="circle"
				/>
			</Tooltip>
		</Box>
	);
};
